import React, { useMemo } from 'react';
import { Box, Stack } from '@mui/material';

import { IStore } from '@ocx-app/modules/stores/types';

import { NoStoresFound, SizingMultiplier } from '@ocx/ui';

import { StorePreview } from '../store-preview.component';
import { MapControls } from './map-controls.component';

type MapControlContainerProps = {
  selectedStore: IStore | null;
  isNoStoresFound: boolean;
  isLocationGranted: boolean;
  onNavigateToListViewClick(): void;
  onStoreClick(storeId: string): void;
  onCurrentLocationClick(): void;
  disabledControls: boolean;
};

export const MapControlContainer: React.FC<MapControlContainerProps> = (props) => {
  const {
    selectedStore,
    onStoreClick,
    onNavigateToListViewClick,
    onCurrentLocationClick,
    isNoStoresFound,
    isLocationGranted,
    disabledControls,
  } = props;

  const opened = useMemo<boolean>(
    () => Boolean(selectedStore || (isNoStoresFound && !selectedStore)),
    [isNoStoresFound, selectedStore],
  );

  return (
    <Box
      position="absolute"
      bottom={0}
      left={0}
      right={0}
      display="flex"
      flexDirection="column"
      sx={{
        transition: 'all 200ms',
        transitionDelay: '100ms',
        ...(opened && {
          bottom: (theme) => `calc(var(--ion-safe-area-bottom, 0px) + ${theme.spacing(SizingMultiplier.xl)})`,
        }),
      }}>
      <Stack
        px={SizingMultiplier.sm}
        sx={{
          // calculation of the max possible size for the popup,
          // to avoid UI overlapping, if the popup hosts huge content
          maxHeight: (theme) => {
            const reservedSpacing = [
              6, // Search bar height, 6 * 8px = 48px
              4, // Search vertical padding, (2[pt] + 2[pb]) * 8px = 32px
              18, // Map control height, 2 x (7[button height] + 1[mt] + 1[mb]) * 8px = 144px
              2, // Map control bottom offset, 2 * 8px = 16px
              SizingMultiplier.xl, // Popup bottom offset
            ].reduce((acc, v) => acc + v);

            return `calc(100vh - var(--ion-safe-area-top, 0px) - var(--ion-safe-area-bottom, 0px) - ${
              theme.mixins.toolbar.minHeight // toolbar height
            }px - ${theme.spacing(reservedSpacing)})`;
          },
        }}>
        <StorePreview delay={200} store={selectedStore} onStoreClick={onStoreClick} showDirection={isLocationGranted} />
        <NoStoresFound delay={200} open={isNoStoresFound && !selectedStore} />
      </Stack>
      <Box position="absolute" top={0} right={0}>
        <MapControls
          offset={!opened}
          onLocationClick={onCurrentLocationClick}
          onListClick={onNavigateToListViewClick}
          disabled={disabledControls}
        />
      </Box>
    </Box>
  );
};
